import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { cancelarAmistad, getChatFriends } from "../../services/api";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import countriesData from "../../data/countries";
import { useWebSocketActions } from "../../context/WebSocketContext";
import { CalendarDays } from "lucide-react";


const getCountryFlag = (countryCode) => {
  if (typeof countryCode !== "string") return "";

  const code = countryCode.trim().toUpperCase();

  if (!/^[A-Z]{2}$/.test(code)) return "";

  return String.fromCodePoint(
    ...Array.from(code).map(
      (letter) => 127397 + letter.charCodeAt(0)
    )
  );
};

const SearchFriendsPage = () => {
  const [amigos, setAmigos] = useState([]);
  const [eliminandoId, setEliminandoId] = useState(null);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { addListener } = useWebSocketActions();

  useEffect(() => {
    fetchAmigos();
  }, []);

  const fetchAmigos = async () => {
    try {
      const res = await getChatFriends();
      setAmigos(res.data);
    } catch (err) {
      console.error("Error cargando amigos", err);
      toast.error(
        t("friends.load_error", "No se pudieron cargar los amigos.")
      );
    }
  };

  useEffect(() => {
    const remove = addListener((data) => {
      if (data.type !== "notification") return;

      const { type } = data.data;

      if (
        type === "friendship_cancelled" ||
        type === "friend_request_accepted"
      ) {
        fetchAmigos();
      }
    });

    return () => remove();
  }, [addListener]);

  const handleUnfollow = async (id) => {
    if (eliminandoId !== null) return;

    setEliminandoId(id);

    try {
      await cancelarAmistad(id);

      toast.success(
        t("friends.unfollow_success", "Has dejado de seguir a este usuario")
      );

      await fetchAmigos();
    } catch (err) {
      toast.error(
        t("friends.unfollow_error", "Error al dejar de seguir")
      );
    } finally {
      setEliminandoId(null);
    }
  };

  const getCountryLabel = (code) => {
    const found = countriesData.find((c) => c.countryCode === code);
    return found?.countryLabel || code;
  };

  const getCityLabel = (cityCode, countryCode) => {
    const country = countriesData.find((c) => c.countryCode === countryCode);

    return (
      country?.cityCodes.find(
        (city) => city.toLowerCase() === cityCode?.toLowerCase()
      ) || cityCode
    );
  };

  const calcularEdad = (fechaNacimiento) => {
    if (!fechaNacimiento) return null;

    const fecha = new Date(fechaNacimiento);
    if (Number.isNaN(fecha.getTime())) return null;

    const hoy = new Date();
    let edad = hoy.getFullYear() - fecha.getFullYear();

    const mes = hoy.getMonth() - fecha.getMonth();

    if (
      mes < 0 ||
      (mes === 0 && hoy.getDate() < fecha.getDate())
    ) {
      edad--;
    }

    return edad >= 0 && edad <= 120 ? edad : null;
  };

  const getUbicacion = (amigo) => {
    const ciudad = amigo.ciudad
      ? getCityLabel(amigo.ciudad, amigo.country)
      : "";

    const pais = amigo.country
      ? getCountryLabel(amigo.country)
      : "";

    return [ciudad, pais].filter(Boolean).join(", ");
  };

  if (amigos.length === 0) {
    return (
      <p className="mt-10 text-center text-gray-600">
        {t("friends.empty", "No tienes amigos aún.")}
      </p>
    );
  }

  return (
    <div className="mx-auto mt-2 md:mt-10 max-w-3xl px-4">
      <div className="space-y-3">
        {amigos.map((amigo) => (
          <div
            key={amigo.id}
            onClick={() => navigate(`/perfil/${amigo.id}`)}
            className="flex cursor-pointer flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-all duration-200 hover:border-[#B8D8EA] hover:bg-[#F8FCFF] hover:shadow-md sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:p-5"
          >
            <div className="flex min-w-0 items-center gap-4">
              <img
                src={amigo.photo || "/default-avatar.png"}
                alt={amigo.nombre || "Usuario"}
                className="h-14 w-14 shrink-0 rounded-full border border-slate-200 object-cover"
              />

              <div className="min-w-0">
                <p className="break-words text-[17px] font-semibold leading-snug text-[#247BA8]">
                  {[amigo.nombre, amigo.apellidos].filter(Boolean).join(" ")}
                </p>

                {getUbicacion(amigo) && (
                  <p className="mt-1.5 flex items-start gap-1.5 text-sm leading-5 text-slate-500">
                    {getCountryFlag(amigo.country) && (
                      <span
                        className="shrink-0 text-base leading-5"
                        aria-hidden="true"
                      >
                        {getCountryFlag(amigo.country)}
                      </span>
                    )}
                    <span>{getUbicacion(amigo)}</span>
                  </p>
                )}

                {calcularEdad(amigo.date_of_birth) !== null && (
                  <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-500">
                    <CalendarDays
                      size={15}
                      className="shrink-0 text-[#247BA8]"
                    />
                    <span>
                      {calcularEdad(amigo.date_of_birth)}{" "}
                      {t("profile.years_old", "años")}
                    </span>
                  </p>
                )}
              </div>
            </div>

            <button
              type="button"
              disabled={eliminandoId !== null}
              onClick={(e) => {
                e.stopPropagation();
                handleUnfollow(amigo.id);
              }}
              className="self-start whitespace-nowrap rounded-xl border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-600 transition-colors hover:border-red-300 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 sm:self-auto"
            >
              {eliminandoId === amigo.id
                ? t("friends.unfollowing", "Procesando...")
                : t("friends.unfollow_button", "Dejar de seguir")}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SearchFriendsPage;
